import * as React from "react";
import useApi from "@/hooks/useApi";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";

interface QnAPair {
  id: string;
  question: string;
  answer: string;
}

interface BotStatus {
  vectorEnabled: boolean;
  enableKbBotIndexing: boolean;
  hasAI: boolean;
}

export default function SquareBotKnowledge() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [status, setStatus] = React.useState<BotStatus | null>(null);
  const [pairs, setPairs] = React.useState<QnAPair[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savedCount, setSavedCount] = React.useState<number | null>(null);
  const [fetchingMerchantData, setFetchingMerchantData] = React.useState(false);
  const [merchantFetchResult, setMerchantFetchResult] = React.useState<
    null | { count: number } | { error: string }
  >(null);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [statusResp, qnaResp] = await Promise.all([
          api.admin.get("SquareBot/Status"),
          api.admin.get("SquareBot/SuggestedQnA"),
        ]);
        const statusData = await statusResp.json();
        const qnaData = await qnaResp.json();
        setStatus(statusData ?? { vectorEnabled: false, enableKbBotIndexing: false, hasAI: false });
        if (Array.isArray(qnaData)) {
          setPairs(
            qnaData.map((t: any) => ({
              id: t.id ?? `pair_${Math.random()}`,
              question: t.question ?? "",
              answer: t.answer ?? "",
            }))
          );
        }
      } catch {
        setStatus({ vectorEnabled: false, enableKbBotIndexing: false, hasAI: false });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function fetchMerchantData() {
    setFetchingMerchantData(true);
    setMerchantFetchResult(null);
    try {
      const resp = await api.admin.get("SquareBot/FetchMerchantData");
      const data = await resp.json();
      if (data.success && Array.isArray(data.pairs)) {
        let filled = 0;
        setPairs((prev) =>
          prev.map((existing) => {
            const match = (data.pairs as QnAPair[]).find((p) => p.id === existing.id);
            if (match && existing.answer.trim() === "" && match.answer.trim() !== "") {
              filled++;
              return { ...existing, answer: match.answer };
            }
            return existing;
          })
        );
        setMerchantFetchResult({ count: filled });
      } else {
        setMerchantFetchResult({ error: data.message || "Could not reach your Square account." });
      }
    } catch {
      setMerchantFetchResult({
        error: "Could not reach your Square account. Make sure your Square connection is active and try again.",
      });
    } finally {
      setFetchingMerchantData(false);
    }
  }

  async function saveToBot() {
    const filledPairs = pairs.filter((p) => p.answer.trim().length > 0);
    if (filledPairs.length === 0) {
      toastAlert.displayToast(Severity.Warning, "Fill in at least one answer before saving.");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.admin.post(
        "SquareBot/PushQnA",
        filledPairs.map((p) => ({ question: p.question, answer: p.answer }))
      );
      const data = await resp.json();
      if (data.success) {
        setSavedCount(data.pushed);
        toastAlert.displayToast(Severity.Success, data.message || `${data.pushed} pairs saved to bot.`);
      } else {
        toastAlert.displayToast(Severity.Error, data.message || "Failed to save.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save to bot.");
    } finally {
      setSaving(false);
    }
  }

  function addCustomPair() {
    setPairs((prev) => [...prev, { id: `custom_${Date.now()}`, question: "", answer: "" }]);
  }

  function removePair(id: string) {
    setPairs((prev) => prev.filter((p) => p.id !== id));
  }

  function updatePair(id: string, field: "question" | "answer", value: string) {
    setPairs((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const noAI = !status?.hasAI;
  const noVector = !status?.vectorEnabled;
  const filledCount = pairs.filter((p) => p.answer.trim().length > 0).length;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
              Bot Knowledge
              {savedCount !== null && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  {savedCount} pairs active
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Teach your bot to answer the questions Square customers ask most. Auto-fill imports
              your business hours, location, phone number, and services from your connected Square
              account.
            </p>
          </div>
        </div>

        {noAI && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>AI not enabled on your plan.</strong> Upgrade to enable the bot knowledge feature.
          </div>
        )}
        {!noAI && noVector && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>Vector index not configured.</strong> Contact support to enable bot knowledge indexing.
          </div>
        )}
      </div>

      {/* Auto-fill from Square */}
      <div className="bg-white rounded-2xl shadow-md p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-700 flex items-center gap-2">
            <span aria-hidden="true">🟦</span>
            Auto-fill from Square
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Pull your business hours, address, phone number, and services directly from your
            connected Square account — no copy/paste needed.
          </p>
          {merchantFetchResult !== null && "count" in merchantFetchResult && (
            <p className="mt-1.5 text-sm text-green-700 font-medium">
              Filled {merchantFetchResult.count} of {pairs.length} answers from your Square account.
            </p>
          )}
          {merchantFetchResult !== null && "error" in merchantFetchResult && (
            <p className="mt-1.5 text-sm text-amber-700 font-medium">
              {merchantFetchResult.error}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={fetchingMerchantData}
          onClick={fetchMerchantData}
          className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            fetchingMerchantData
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : "bg-slate-800 text-white hover:bg-slate-900"
          }`}
        >
          {fetchingMerchantData ? "Fetching data..." : "Auto-fill from Square →"}
        </button>
      </div>

      {/* Step 1 — QnA Builder */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-semibold text-slate-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">
                1
              </span>
              Quick Q&amp;A — your business's most common questions
            </div>
            <p className="text-sm text-slate-500 mt-1 ml-8">
              Fill in your answers below. The bot will use these to answer customers instantly.
              Leave blank any questions that don't apply to your business.
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <span className="text-xs text-slate-400">
              {filledCount}/{pairs.length} answered
            </span>
            <button
              type="button"
              disabled={saving || filledCount === 0}
              onClick={saveToBot}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                saving || filledCount === 0
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {saving ? "Saving..." : "Save to bot"}
            </button>
          </div>
        </div>

        <div className="space-y-3 ml-8">
          {pairs.map((pair) => (
            <div key={pair.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  {pair.id.startsWith("custom_") ? (
                    <input
                      type="text"
                      value={pair.question}
                      onChange={(e) => updatePair(pair.id, "question", e.target.value)}
                      placeholder="Type your question..."
                      className="w-full text-sm font-medium text-slate-700 bg-transparent border-b border-slate-200 pb-1 mb-2 focus:outline-none focus:border-blue-400"
                    />
                  ) : (
                    <div className="text-sm font-medium text-slate-700 mb-2">{pair.question}</div>
                  )}
                  <textarea
                    rows={3}
                    value={pair.answer}
                    onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
                    placeholder="Type your answer here — be specific about your business..."
                    className="w-full text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  />
                </div>
                {pair.id.startsWith("custom_") && (
                  <button
                    type="button"
                    onClick={() => removePair(pair.id)}
                    aria-label="Remove pair"
                    className="text-slate-300 hover:text-red-400 mt-1 flex-shrink-0"
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addCustomPair}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
          >
            <svg
              viewBox="0 0 16 16"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add custom question
          </button>
        </div>

        <div className="mt-4 ml-8 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <strong>Tip:</strong> The more specific your answers, the better. Instead of "See our
          website", say "We accept walk-ins based on availability. Booking ahead is recommended
          for weekends and peak hours." Specific answers get much better bot retrieval scores.
        </div>
      </div>

      {/* Step 2 — Power Features */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">
            2
          </span>
          Power features — beyond basic Q&amp;A
        </div>
        <p className="text-sm text-slate-500 ml-8 mb-4">
          Your bot can go far beyond answering common questions. These AI skills let it book
          appointments, look up orders, check loyalty points, process refunds, and more — all
          in the chat window.
        </p>
        <div className="ml-8 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            {
              label: "Appointment Booking",
              tool: "square_book_appointment",
              desc: "Customer asks to book — bot checks your live Square availability, collects name and email, and creates the booking directly on your calendar.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="4" width="14" height="13" rx="2" /><path d="M7 2v4M13 2v4M3 9h14" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Order Lookup",
              tool: "square_search_orders",
              desc: "Customer asks about their order — bot finds it by email or phone and returns status, line items, and totals from Square.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 10h14M10 3l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Loyalty Points",
              tool: "square_get_loyalty_balance",
              desc: "Customer asks how many points they have — bot fetches live balance and available rewards from Square Loyalty.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M10 2l2.4 5h5.2l-4.2 3.1 1.6 5L10 12l-5 3.1 1.6-5L2.4 7h5.2z" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Gift Card Balance",
              tool: "square_check_gift_card",
              desc: "Customer asks about a gift card — bot reads the balance by Gift Account Number in seconds, no store visit needed.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="2" y="5" width="16" height="10" rx="2" /><path d="M2 10h16M7 5v10" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Catalog Search",
              tool: "square_search_catalog",
              desc: "Customer asks about products or services — bot searches your Square catalog by keyword and returns name, price, and availability.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="9" cy="9" r="6" /><path d="M15 15l3 3" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Refund Processing",
              tool: "square_create_refund",
              desc: "Customer requests a refund — bot verifies the payment and issues the refund directly in Square with confirmation.",
              tier: "Commerce Pro",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 10a7 7 0 1 0 14 0H3M10 3l-4 4 4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Payment Link Generator",
              tool: "square_create_checkout_link",
              desc: "Send a Square checkout link directly in chat — great for deposits, quotes, and phone orders. Customer pays without visiting the store.",
              tier: "Commerce Pro",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="2" /><path d="M7 10h6M7 13h4M7 7h6" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Invoice Lookup",
              tool: "square_list_invoices",
              desc: "B2B customers ask about outstanding invoices — bot lists open and paid Square invoices with due dates and amounts.",
              tier: "Commerce Pro",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="4" y="2" width="12" height="16" rx="1" /><path d="M7 6h6M7 9h6M7 12h4" strokeLinecap="round" />
                </svg>
              ),
            },
          ].map((f, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-4 flex gap-3">
              <div className="text-blue-500 mt-0.5 flex-shrink-0">{f.icon}</div>
              <div>
                <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  {f.label}
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    f.tier === "Commerce Pro"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-amber-100 text-amber-700"
                  }`}>
                    {f.tier}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{f.desc}</div>
                <div className="text-xs text-slate-400 mt-1 font-mono">{f.tool}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="ml-8 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
          <strong>To enable:</strong> Go to <strong>Bots &rarr; AI Training &rarr; Skills</strong> and enable the
          Square Commerce skills. Professional skills require a Professional plan. Commerce Pro skills require
          a Pro plan. Then use the <strong>Launch Starter Wizard</strong> (Setup tab) to add a Square workflow.
        </div>
      </div>

      {/* Step 3 — Analytics */}
      <BotAnalyticsPanel api={api} />
    </div>
  );
}

function BotAnalyticsPanel({ api }: { api: ReturnType<typeof useApi> }) {
  const [summary, setSummary] = React.useState<{
    totalSkillCalls: number;
    errorCount: number;
    successRate: number;
    topSkill: string;
    topSkillCount: number;
    conversationsTrainedCount: number;
  } | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const resp = await api.admin.get("BotAnalytics/Summary?days=30");
        const data = await resp.json();
        setSummary(data);
      } catch { /* ignore */ }
    })();
  }, []);

  if (!summary || summary.totalSkillCalls === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white">
      <div className="flex items-center gap-2 mb-3">
        <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" className="text-blue-500">
          <rect x="3" y="10" width="3" height="7" /><rect x="8.5" y="6" width="3" height="11" /><rect x="14" y="3" width="3" height="14" />
        </svg>
        <span className="text-sm font-semibold text-slate-700">Bot performance — last 30 days</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Skill calls", value: summary.totalSkillCalls.toLocaleString() },
          { label: "Success rate", value: `${summary.successRate}%` },
          { label: "Top skill", value: summary.topSkill.replace("square_", "").replace(/_/g, " ") },
          { label: "Chats trained", value: summary.conversationsTrainedCount.toLocaleString() }
        ].map((stat, i) => (
          <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
            <div className="text-lg font-bold text-slate-800">{stat.value}</div>
            <div className="text-xs text-slate-400 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
